class Slider {
  constructor(slider, options = {}) {
    this.track = slider;
    this.options = options || {};
    this.trackWidth = 0;

    this.viewport =
      this.options.viewport ||
      slider.closest("[data-slider-viewport]") ||
      slider.parentElement ||
      slider;
    this.root =
      this.options.root ||
      slider.closest("[data-slider-root]") ||
      this.viewport.parentElement ||
      this.viewport;

    this.mode = this._getStringOption("mode", "slide", ["mode", "sliderEffect"]);
    this.isFade = this.mode === "fade";

    this.hasDots = this._getBooleanOption("dots", slider.hasAttribute("data-dots"));
    this.infinite = this._getBooleanOption(
      "infinite",
      slider.hasAttribute("data-infinite"),
    );
    this.hasButtons = this._getBooleanOption(
      "buttons",
      slider.hasAttribute("data-buttons"),
    );

    this.buttons = Array.from(this.root.querySelectorAll("[data-slider-button]"));
    this.prevButton = this.root.querySelector("[data-slider-prev]");
    this.nextButton = this.root.querySelector("[data-slider-next]");

    this.hasArrows =
      !!(this.prevButton || this.nextButton) &&
      this._getBooleanOption("arrows", true);
    this.index = 0;
    this.visualIndex = 0;
    this.pointer = { downX: 0, curX: 0, dragging: false, dragCanceled: false };
    this.samples = [];
    this.vx = 0;
    this.transitionMs = this._getNumberOption("speed", 400, [
      "speed",
      "transitionMs",
    ]);
    this.oldCellStep = 0;
    this.base = 0;
    this.wraps = 0;
    this.thresholdRatio = 0.12;
    this.dragThresh = 6;
    this._move = this.onPointerMove.bind(this);
    this._up = this.onPointerUp.bind(this);
    this._resize = this.onResize.bind(this);
    this._changeListeners = [];
    this._destroyed = false;
    this._isSetup = false;

    this.autoplayEnabled = this._getBooleanOption("autoplay", false);
    this.autoplayDelay = this._getNumberOption("autoplayDelay", 4000, [
      "autoplayDelay",
      "autoplaySpeed",
    ]);
    this.pauseOnHover = this._getBooleanOption("pauseOnHover", true);
    this.pauseOnFocus = this._getBooleanOption("pauseOnFocus", true);
    this.pauseOnInteraction = this._getBooleanOption("pauseOnInteraction", true);
    this.stopAutoplayOnLast = this._getBooleanOption(
      "stopAutoplayOnLast",
      !this.infinite,
    );
    this._autoplayTimer = null;
    this._autoplayPauses = new Set();

    this.slidesToScroll = Math.max(
      1,
      Math.round(this._getNumberOption("slidesToScroll", 1)),
    );
    this.visibleCountOverride = null;

    this.progressSelector = this._getStringOption(
      "progressSelector",
      ".slider-progress",
    );
    this.progressIndicatorClass = this._getStringOption(
      "progressIndicatorClass",
      "slider-progress__fill",
    );
    const progressHostExists = !!(
      (this.root && this.root.querySelector(this.progressSelector)) ||
      (this.viewport && this.viewport.querySelector(this.progressSelector))
    );
    this.progressEnabled = this._getBooleanOption("progress", progressHostExists);

    this.responsive = this._normalizeResponsive(
      this._getRawOption(["responsive"], slider.dataset.responsive),
    );
    this._activeBreakpoint = null;
    this._baseRuntimeSettings = {
      dots: this.hasDots,
      infinite: this.infinite,
      buttons: this.hasButtons,
      arrows: this.hasArrows,
      speed: this.transitionMs,
      autoplay: this.autoplayEnabled,
      autoplayDelay: this.autoplayDelay,
      pauseOnHover: this.pauseOnHover,
      pauseOnFocus: this.pauseOnFocus,
      pauseOnInteraction: this.pauseOnInteraction,
      stopAutoplayOnLast: this.stopAutoplayOnLast,
      slidesToScroll: this.slidesToScroll,
      slidesToShow: null,
      progress: this.progressEnabled,
    };
    this._applyResponsiveSettings(true);

    this.setup();
  }

  _getDataValues(keys = []) {
    return keys
      .map((key) => this.track.dataset[key])
      .filter((value) => value !== undefined);
  }

  _getRawOption(keys = [], fallback) {
    for (const key of keys) {
      if (this.options[key] !== undefined) return this.options[key];
    }
    const dataValues = this._getDataValues(keys);
    if (dataValues.length) return dataValues[0];
    return fallback;
  }

  _coerceBoolean(value, fallback) {
    if (value === undefined || value === null) return fallback;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    const normalized = String(value).trim().toLowerCase();
    if (["false", "0", "off", "no"].includes(normalized)) return false;
    if (["true", "1", "on", "yes", ""].includes(normalized)) return true;
    return fallback;
  }

  _coerceNumber(value, fallback) {
    if (value === undefined || value === null || value === "") return fallback;
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  _getBooleanOption(key, fallback) {
    return this._coerceBoolean(this._getRawOption([key], fallback), fallback);
  }

  _getNumberOption(key, fallback, aliases = []) {
    return this._coerceNumber(this._getRawOption([key, ...aliases], fallback), fallback);
  }

  _getStringOption(key, fallback, aliases = []) {
    const raw = this._getRawOption([key, ...aliases], fallback);
    return raw == null ? fallback : String(raw);
  }

  _normalizeResponsive(raw) {
    if (!raw) return [];

    let items = raw;
    if (typeof raw === "string") {
      try {
        items = JSON.parse(raw);
      } catch {
        return [];
      }
    }
    if (!Array.isArray(items)) return [];

    return items
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const breakpoint = Number(entry.breakpoint);
        if (!Number.isFinite(breakpoint)) return null;
        return {
          breakpoint,
          settings:
            entry.settings === undefined || entry.settings === null
              ? {}
              : entry.settings,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.breakpoint - b.breakpoint);
  }

  _resolveResponsiveEntry() {
    if (!this.responsive.length || typeof window === "undefined") return null;
    const width = window.innerWidth || document.documentElement.clientWidth || 0;

    for (const entry of this.responsive) {
      if (width <= entry.breakpoint) return entry;
    }
    return null;
  }

  _coerceResponsiveBool(settings, key, current) {
    if (!settings || settings[key] === undefined) return current;
    return this._coerceBoolean(settings[key], current);
  }

  _coerceResponsiveNumber(settings, keys, current, integer = false) {
    const list = Array.isArray(keys) ? keys : [keys];
    for (const key of list) {
      if (!settings || settings[key] === undefined) continue;
      let value = this._coerceNumber(settings[key], current);
      if (integer) value = Math.round(value);
      return value;
    }
    return current;
  }

  _applyResponsiveSettings(isInitial = false) {
    const entry = this._resolveResponsiveEntry();
    this._activeBreakpoint = entry ? entry.breakpoint : null;

    if (entry && entry.settings === "unslick") {
      return;
    }

    const settings = entry && typeof entry.settings === "object" ? entry.settings : {};
    const base = this._baseRuntimeSettings || {};

    const prev = {
      hasDots: this.hasDots,
      hasArrows: this.hasArrows,
      autoplayEnabled: this.autoplayEnabled,
      autoplayDelay: this.autoplayDelay,
      pauseOnHover: this.pauseOnHover,
      pauseOnFocus: this.pauseOnFocus,
      pauseOnInteraction: this.pauseOnInteraction,
      stopAutoplayOnLast: this.stopAutoplayOnLast,
      progressEnabled: this.progressEnabled,
      slidesToScroll: this.slidesToScroll,
      visibleCountOverride: this.visibleCountOverride,
      transitionMs: this.transitionMs,
    };

    this.hasDots = this._coerceResponsiveBool(settings, "dots", base.dots);
    this.infinite = this._coerceResponsiveBool(settings, "infinite", base.infinite);
    this.hasButtons = this._coerceResponsiveBool(settings, "buttons", base.buttons);
    const arrowsEnabled = this._coerceResponsiveBool(settings, "arrows", base.arrows);
    this.hasArrows = !!(this.prevButton || this.nextButton) && arrowsEnabled;

    this.transitionMs = this._coerceResponsiveNumber(
      settings,
      ["speed", "transitionMs"],
      base.speed,
      true,
    );

    this.autoplayEnabled = this._coerceResponsiveBool(
      settings,
      "autoplay",
      base.autoplay,
    );
    this.autoplayDelay = this._coerceResponsiveNumber(
      settings,
      ["autoplayDelay", "autoplaySpeed"],
      base.autoplayDelay,
      true,
    );
    this.pauseOnHover = this._coerceResponsiveBool(
      settings,
      "pauseOnHover",
      base.pauseOnHover,
    );
    this.pauseOnFocus = this._coerceResponsiveBool(
      settings,
      "pauseOnFocus",
      base.pauseOnFocus,
    );
    this.pauseOnInteraction = this._coerceResponsiveBool(
      settings,
      "pauseOnInteraction",
      base.pauseOnInteraction,
    );
    this.stopAutoplayOnLast = this._coerceResponsiveBool(
      settings,
      "stopAutoplayOnLast",
      base.stopAutoplayOnLast,
    );

    this.slidesToScroll = Math.max(
      1,
      this._coerceResponsiveNumber(
        settings,
        "slidesToScroll",
        base.slidesToScroll,
        true,
      ),
    );

    const slidesToShow = this._coerceResponsiveNumber(
      settings,
      "slidesToShow",
      base.slidesToShow,
      true,
    );
    this.visibleCountOverride =
      Number.isFinite(slidesToShow) && slidesToShow > 0 ? slidesToShow : null;

    this.progressEnabled = this._coerceResponsiveBool(
      settings,
      "progress",
      base.progress,
    );

    if (!this._isSetup || isInitial) return;

    if (prev.hasArrows !== this.hasArrows) {
      this._syncArrowVisibility();
      this._updateArrowState();
    } else {
      this._syncArrowVisibility();
    }

    if (prev.hasDots !== this.hasDots) {
      if (this.hasDots) this.initDots();
      else this.clearDots();
    }

    if (prev.progressEnabled !== this.progressEnabled) {
      if (this.progressEnabled) this.initProgress();
      else this.clearProgress();
    }

    const autoplayConfigChanged =
      prev.autoplayEnabled !== this.autoplayEnabled ||
      prev.autoplayDelay !== this.autoplayDelay ||
      prev.pauseOnHover !== this.pauseOnHover ||
      prev.pauseOnFocus !== this.pauseOnFocus;

    if (autoplayConfigChanged) {
      this._clearAutoplayTimer();
      this._teardownAutoplayBindings();
      this._setupAutoplayBindings();
      if (this.autoplayEnabled) this._scheduleAutoplay();
    }
  }

  _normalizeIndex(index) {
    if (!this.slides || !this.slides.length) return 0;
    return ((index % this.slides.length) + this.slides.length) % this.slides.length;
  }

  _setupArrows() {
    this._arrowHandlers = {
      prev: () => {
        if (!this.hasArrows) return;
        if (this.infinite) {
          this.goTo(this.index - this.slidesToScroll);
        } else if (this.index > 0) {
          this.goTo(this.index - this.slidesToScroll);
        }
      },
      next: () => {
        if (!this.hasArrows) return;
        if (this.infinite) {
          this.goTo(this.index + this.slidesToScroll);
        } else if (this.index < this.maxIndex) {
          this.goTo(this.index + this.slidesToScroll);
        }
      },
    };

    if (this.prevButton) {
      this.prevButton.addEventListener("click", this._arrowHandlers.prev);
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", this._arrowHandlers.next);
    }

    this._updateArrowState();
    this._syncArrowVisibility();
  }

  _updateArrowState() {
    if (!this.hasArrows) return;
    if (this.infinite) {
      if (this.prevButton) this.prevButton.disabled = false;
      if (this.nextButton) this.nextButton.disabled = false;
      return;
    }

    if (this.prevButton) {
      this.prevButton.disabled = this.index === 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled = this.index === this.maxIndex;
    }
  }

  _syncArrowVisibility() {
    [this.prevButton, this.nextButton].forEach((button) => {
      if (!button) return;
      button.hidden = !this.hasArrows;
      button.setAttribute("aria-hidden", this.hasArrows ? "false" : "true");
    });
  }

  _applyFadeLayout() {
    this.track.style.display = "grid";
    this.track.style.gridTemplateColumns = "1fr";

    this.slides.forEach((slide) => {
      slide.style.gridArea = "1 / 1";
      slide.style.transform = "none";
      slide.style.willChange = "opacity";
    });
  }

  _setupAutoplayBindings() {
    if (!this.autoplayEnabled) return;

    const target = this.viewport || this.track;
    this._autoplayEvents = this._autoplayEvents || {};

    if (this.pauseOnHover) {
      this._autoplayEvents.mouseenter = () => this.pauseAutoplay("hover");
      this._autoplayEvents.mouseleave = () => this.resumeAutoplay("hover");
      target.addEventListener("mouseenter", this._autoplayEvents.mouseenter);
      target.addEventListener("mouseleave", this._autoplayEvents.mouseleave);
    }

    if (this.pauseOnFocus) {
      this._autoplayEvents.focusin = () => this.pauseAutoplay("focus");
      this._autoplayEvents.focusout = () => this.resumeAutoplay("focus");
      target.addEventListener("focusin", this._autoplayEvents.focusin);
      target.addEventListener("focusout", this._autoplayEvents.focusout);
    }

    this._autoplayEvents.visibility = () => {
      if (document.hidden) this.pauseAutoplay("visibility");
      else this.resumeAutoplay("visibility");
    };
    document.addEventListener(
      "visibilitychange",
      this._autoplayEvents.visibility,
      false,
    );
  }

  _teardownAutoplayBindings() {
    if (!this._autoplayEvents) return;
    const target = this.viewport || this.track;

    if (this._autoplayEvents.mouseenter) {
      target.removeEventListener("mouseenter", this._autoplayEvents.mouseenter);
      target.removeEventListener("mouseleave", this._autoplayEvents.mouseleave);
    }
    if (this._autoplayEvents.focusin) {
      target.removeEventListener("focusin", this._autoplayEvents.focusin);
      target.removeEventListener("focusout", this._autoplayEvents.focusout);
    }
    if (this._autoplayEvents.visibility) {
      document.removeEventListener(
        "visibilitychange",
        this._autoplayEvents.visibility,
        false,
      );
    }
    this._autoplayEvents = null;
  }

  _emitChange(source = "goTo") {
    const detail = {
      index: this.index,
      logicalIndex: this.index,
      slideIndex: this._normalizeIndex(this.index),
      instance: this,
      source,
      track: this.track,
    };

    this._changeListeners.forEach((listener) => {
      try {
        listener(detail);
      } catch (err) {
        console.error(err);
      }
    });

    this.track.dispatchEvent(new CustomEvent("slider:change", { detail }));
    document.dispatchEvent(new CustomEvent("sliderChange", { detail }));
  }

  onChange(listener) {
    if (typeof listener !== "function") return () => {};
    this._changeListeners.push(listener);
    return () => {
      this._changeListeners = this._changeListeners.filter((fn) => fn !== listener);
    };
  }

  setup() {
    this.refreshSlides();
    this.measure();
    this.visualIndex = this.index;

    if (!this.slides.length) {
      window.addEventListener("resize", this._resize);
      return;
    }

    if (this.isFade) {
      this._applyFadeLayout();
    }

    if (this.hasDots) this.initDots();
    if (this.progressEnabled) this.initProgress();

    if (this.hasButtons && !this.infinite) {
      this._btnHandlers = this.buttons.map((b, idx) => () => {
        const index = parseInt(b.getAttribute("data-index"), 10);
        this.goTo(index);
        this.buttons.forEach((btn, i) => btn.classList.toggle("active", i === idx));
      });
      this.buttons.forEach((b, i) => b.addEventListener("click", this._btnHandlers[i]));
    }

    if (this.prevButton || this.nextButton) {
      this._setupArrows();
    }

    if (this.infinite && !this.isFade) this.wrapInfiniteInit();

    this.setTransition(false);
    this.applyTransform(0);
    requestAnimationFrame(() => this.setTransition(true));

    if (this.slides.length > 1) {
      this._down = this._down || this.onPointerDown.bind(this);
      if (!this.track.style.touchAction) this.track.style.touchAction = "pan-y";
      this.track.addEventListener("pointerdown", this._down);
    }

    window.addEventListener("resize", this._resize);

    this.track.querySelectorAll("img").forEach((img) => (img.draggable = false));

    this._setupAutoplayBindings();
    if (this.autoplayEnabled) this.startAutoplay();
    this._isSetup = true;
  }

  switchButtons(index) {
    this.buttons.forEach((btn, bbb) => {
      btn.classList.toggle("active", bbb === index);
    });
  }

  initDots() {
    this.dotsContainer =
      this.root.querySelector(".dots") || this.viewport.querySelector(".dots");
    if (!this.dotsContainer) return;

    this.dotsContainer.replaceChildren();

    const mapArray = !this.infinite
      ? [...this.slides].slice(0, this.slides.length - this.visibleCount + 1)
      : [...this.slides];

    mapArray.forEach((_, i) => {
      const dot = document.createElement("div");
      dot.classList.add("dot");
      dot.setAttribute("role", "button");
      dot.setAttribute("tabindex", "0");
      if (i === 0) dot.classList.add("active");
      this.dotsContainer.appendChild(dot);
    });

    this.dots = Array.from(this.dotsContainer.children);
    this._dotHandlers = this.dots.map((dot, i) => {
      const onClick = () => this.goTo(i, true, "dot");
      const onKeydown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          this.goTo(i, true, "dot");
        }
      };

      dot.addEventListener("click", onClick);
      dot.addEventListener("keydown", onKeydown);
      return { onClick, onKeydown };
    });
  }

  clearDots() {
    if (!this.dotsContainer) return;
    if (this._dotHandlers && this.dots) {
      this.dots.forEach((dot, i) => {
        const handlers = this._dotHandlers[i];
        if (!handlers) return;
        dot.removeEventListener("click", handlers.onClick);
        dot.removeEventListener("keydown", handlers.onKeydown);
      });
    }
    this.dotsContainer.replaceChildren();
    this.dots = [];
    this._dotHandlers = null;
  }

  switchDots(index) {
    if (!this.dots || !this.dots.length) return;
    const activeIndex = this._normalizeIndex(index);

    this.dots.forEach((d, dd) => d.classList.toggle("active", dd === activeIndex));

    this.slides.forEach((slide, slideIndex) => {
      slide.classList.toggle("active", slideIndex === activeIndex);
    });
  }

  initProgress() {
    this.progressContainer =
      this.root.querySelector(this.progressSelector) ||
      this.viewport.querySelector(this.progressSelector);
    if (!this.progressContainer) return;

    const existing =
      this.progressContainer.querySelector(`.${this.progressIndicatorClass}`) ||
      this.progressContainer.querySelector(".slider-progress__fill") ||
      this.progressContainer.querySelector(".slider-progress__indicator");

    this.progressIndicator = existing || document.createElement("div");

    if (!existing) {
      this.progressIndicator.className = this.progressIndicatorClass;
      this.progressContainer.appendChild(this.progressIndicator);
      this._createdProgressIndicator = true;
    } else {
      this._createdProgressIndicator = false;
      if (!this.progressIndicator.classList.contains(this.progressIndicatorClass)) {
        this.progressIndicator.classList.add(this.progressIndicatorClass);
      }
    }

    this.updateProgress();
  }

  clearProgress() {
    if (!this.progressContainer) return;
    if (this.progressIndicator && this._createdProgressIndicator) {
      this.progressIndicator.remove();
    } else if (this.progressIndicator) {
      this.progressIndicator.style.width = "";
      this.progressIndicator.style.transform = "";
    }

    this.progressIndicator = null;
    this.progressContainer = null;
    this._createdProgressIndicator = false;
  }

  getProgressSteps() {
    if (!this.slides || !this.slides.length) return 1;
    if (this.infinite || this.isFade) return Math.max(1, this.slides.length);
    return Math.max(1, this.maxIndex + 1);
  }

  getProgressIndex() {
    if (!this.slides || !this.slides.length) return 0;
    if (this.infinite || this.isFade) return this._normalizeIndex(this.index);
    return Math.max(0, Math.min(this.maxIndex, this.index));
  }

  updateProgress() {
    if (!this.progressEnabled) return;
    if (!this.progressContainer || !this.progressIndicator) {
      this.initProgress();
      if (!this.progressIndicator) return;
    }

    const steps = this.getProgressSteps();
    const idx = Math.min(this.getProgressIndex(), steps - 1);
    this.progressIndicator.style.width = `${100 / steps}%`;
    this.progressIndicator.style.transform = `translateX(${idx * 100}%)`;
  }

  refreshSlides() {
    this.slides = Array.from(this.track.children);
    this.lastIndex = this.slides.length - 1;
  }

  getGapPx() {
    const cs = getComputedStyle(this.track);
    const gap = parseFloat(cs.columnGap || cs.gap || "0");
    return Number.isNaN(gap) ? 0 : gap;
  }

  measure() {
    this.refreshSlides();

    if (!this.slides.length) {
      this.trackWidth = this.track.clientWidth || 0;
      this.cell = { width: 0, gap: 0, step: 1 };
      this.visibleCount = 1;
      this.allSlides = [];
      this.realCount = 0;
      this.maxIndex = 0;
      this.oldCellStep = 1;
      return;
    }

    const first = this.slides[0];
    const rect = first.getBoundingClientRect();
    const gap = this.isFade ? 0 : this.getGapPx();

    this.trackWidth = this.track.clientWidth;

    this.cell = {
      width: rect.width,
      gap,
      step: Math.max(1, rect.width + gap),
    };

    const vpW = (this.viewport || this.track).clientWidth || this.track.clientWidth;

    this.visibleCount = this.isFade
      ? 1
      : this.visibleCountOverride || Math.max(1, Math.floor((vpW + gap) / this.cell.step));

    this.allSlides = Array.from(this.track.children);
    this.realCount = this.allSlides.length;
    this.maxIndex = Math.max(0, this.realCount - this.visibleCount);
    this.oldCellStep = this.cell.step;
  }

  wrapInfiniteInit() {
    if (!this.slides.length) return;

    const { left } = this.slides[0].getBoundingClientRect();
    const trackLeft = this.track.getBoundingClientRect().left;
    const cellsToWrap = Math.ceil((left - trackLeft + 1) / this.cell.step);

    let i = 0;
    this.lastWrappedLeft = {};
    while (i < cellsToWrap) {
      this.lastWrappedLeft.el = this.slides[this.lastIndex];

      this.track.removeChild(this.lastWrappedLeft.el);
      this.track.insertBefore(this.lastWrappedLeft.el, this.slides[0]);
      this.base -= this.cell.step;
      this.wraps--;

      this.applyTransform();
      i++;
    }
    this.refreshSlides();
    this.lastWrappedRight = { el: this.slides[0] };
  }

  wrapInfinite(direction = "left", difference = 1) {
    if (this.isFade || !this.slides.length) return;

    if (direction == "left") {
      for (let i = 0; i < difference; i++) {
        this.lastWrappedLeft.el = this.slides[this.lastIndex];
        this.track.removeChild(this.lastWrappedLeft.el);
        this.track.insertBefore(this.lastWrappedLeft.el, this.slides[0]);
        this.base -= this.cell.step;
        this.wraps--;
        this.refreshSlides();
      }
    } else {
      for (let i = 0; i < difference; i++) {
        this.lastWrappedRight.el = this.slides[0];
        this.track.removeChild(this.lastWrappedRight.el);
        this.track.appendChild(this.lastWrappedRight.el);
        this.base += this.cell.step;
        this.wraps++;
        this.refreshSlides();
      }
    }
  }

  prebufferBySteps(dir = "left", count) {
    if (count <= 0) return;
    this.setTransition(false);
    this.wrapInfinite(dir, count);
    this.applyTransform(0);
  }

  setTransition(on) {
    const v = this.isFade
      ? on
        ? `opacity ${this.transitionMs}ms ease`
        : "none"
      : on
        ? `transform ${this.transitionMs}ms ease`
        : "none";

    this.slides.forEach((s) => {
      s.style.transition = v;
    });
  }

  translateFor(index, dx = 0) {
    if (this.isFade) return;
    const x = -(index * this.cell.step) + dx + this.base;

    this.slides.forEach((s) => {
      s.style.transform = `translate3d(${x}px,0,0)`;
    });
  }

  _renderFade() {
    const activeIndex = this._normalizeIndex(this.index);
    this.slides.forEach((slide, i) => {
      const isActive = i === activeIndex;
      slide.style.opacity = isActive ? "1" : "0";
      slide.style.pointerEvents = isActive ? "auto" : "none";
      slide.style.zIndex = isActive ? "2" : "1";
      slide.classList.toggle("active", isActive);
    });
  }

  applyTransform(dx = 0) {
    if (this.isFade) {
      this._renderFade();
      return;
    }
    this.translateFor(this.visualIndex, dx);
  }

  goTo(logicalIndex, animate = true, source = "goTo") {
    if (!this.slides || !this.slides.length) return;

    if (this.isFade && this.infinite) {
      this.index = this._normalizeIndex(logicalIndex);
    } else {
      this.index = this.infinite
        ? logicalIndex
        : Math.max(0, Math.min(this.maxIndex, logicalIndex));
    }

    this.visualIndex = this.index;

    if (!this.infinite && this.hasButtons) {
      this.switchButtons(this.index);
    }

    this._updateArrowState();

    if (this.hasDots) {
      this.switchDots(this.index);
    }

    this.setTransition(animate);
    this.applyTransform(0);
    this.updateProgress();
    this._emitChange(source);

    if (this.autoplayEnabled) this._scheduleAutoplay();
  }

  next(steps = this.slidesToScroll, source = "next") {
    this.goTo(this.index + steps, true, source);
  }

  prev(steps = this.slidesToScroll, source = "prev") {
    this.goTo(this.index - steps, true, source);
  }

  _scheduleAutoplay() {
    this._clearAutoplayTimer();
    if (
      !this.autoplayEnabled ||
      this._destroyed ||
      this._autoplayPauses.size ||
      !this.slides ||
      this.slides.length < 2
    ) {
      return;
    }

    this._autoplayTimer = window.setTimeout(() => {
      if (!this.autoplayEnabled || this._autoplayPauses.size) return;

      if (!this.infinite) {
        if (this.index >= this.maxIndex) {
          if (this.stopAutoplayOnLast) {
            this.stopAutoplay();
            return;
          }
          this.goTo(0, true, "autoplay");
          return;
        }
      }

      this.next(this.slidesToScroll, "autoplay");
    }, this.autoplayDelay);
  }

  _clearAutoplayTimer() {
    if (this._autoplayTimer) {
      clearTimeout(this._autoplayTimer);
      this._autoplayTimer = null;
    }
  }

  startAutoplay() {
    this.autoplayEnabled = true;
    this._scheduleAutoplay();
  }

  stopAutoplay() {
    this.autoplayEnabled = false;
    this._autoplayPauses.clear();
    this._clearAutoplayTimer();
  }

  pauseAutoplay(reason = "manual") {
    this._autoplayPauses.add(reason);
    this._clearAutoplayTimer();
  }

  resumeAutoplay(reason = "manual") {
    this._autoplayPauses.delete(reason);
    this._scheduleAutoplay();
  }

  play() {
    this.startAutoplay();
  }

  pause() {
    this.pauseAutoplay("manual");
  }

  removePointerFromSlides() {
    this.slides.forEach((s) => {
      s.style.pointerEvents = "none";
    });
  }

  addPointerToSlides() {
    if (this.isFade) {
      this._renderFade();
      return;
    }
    this.slides.forEach((s) => {
      s.style.pointerEvents = "auto";
    });
  }

  onPointerDown(e) {
    if (this.pauseOnInteraction) this.pauseAutoplay("interaction");

    const now = performance.now();
    this.samples = [{ x: e.clientX, t: now }];
    this.pointer.startX = this.pointer.curX = e.clientX;
    this.pointer.startY = e.clientY;
    this.pointer.dragging = false;
    this.pointer.dragCanceled = false;

    this._move = this._move || this.onPointerMove.bind(this);
    this._up = this._up || this.onPointerUp.bind(this);
    this.pointer.startedOnLink = e.target.closest("a") !== null;

    this._suppressClick =
      this._suppressClick ||
      ((ev) => {
        if (this.pointer.dragging) {
          ev.preventDefault();
          ev.stopPropagation();
          ev.stopImmediatePropagation();
          this.removePointerFromSlides();

          setTimeout(() => {
            this.track.removeEventListener("click", this._suppressClick, true);
            this.addPointerToSlides();
          }, 0);
        }
      });

    this.track.addEventListener("pointermove", this._move, { passive: false });
    this.track.addEventListener("pointerup", this._up, { passive: true });
    this.track.addEventListener("pointercancel", this._up, { passive: true });
    this.track.addEventListener("pointerleave", this._up, { passive: true });
    this.track.addEventListener("click", this._suppressClick, true);
  }

  onPointerMove(e) {
    const x = e.clientX;
    const y = e.clientY;

    if (this.pointer.dragCanceled) {
      return;
    }

    if (!this.pointer.dragging) {
      const dx0 = x - this.pointer.startX;
      const dy0 = y - this.pointer.startY;
      if (Math.hypot(dx0, dy0) < this.dragThresh) {
        return;
      }

      // Fade sliders don't support dragged interpolation, but should still swipe.
      // Only commit on primarily horizontal movement so normal page scroll works.
      if (this.isFade && Math.abs(dx0) <= Math.abs(dy0)) {
        this.pointer.dragCanceled = true;
        return;
      }

      this.pointer.dragging = true;
      this.track.classList.add("is-dragging");
      if (!this.isFade) {
        this.setTransition(false);
      }

      if (this.pointer.startedOnLink) {
        e.preventDefault();
      }

      try {
        this.track.setPointerCapture(e.pointerId);
      } catch {}

      this.pointer.downX = x;
      this.samples = [{ x, t: performance.now() }];
    }

    e.preventDefault();

    this.pointer.curX = x;
    const dx = this.pointer.curX - this.pointer.downX;
    if (!this.isFade) {
      this.applyTransform(dx);
    }

    const now = performance.now();
    this.samples.push({ x, t: now });
    while (
      this.samples.length > 5 ||
      (this.samples.length && now - this.samples[0].t > 150)
    ) {
      this.samples.shift();
    }
    if (this.samples.length >= 2) {
      const a = this.samples[0];
      const b = this.samples[this.samples.length - 1];
      const dt = b.t - a.t;
      this.vx = dt > 0 ? (b.x - a.x) / (dt * 2) : 0;
    }

    if (!this.isFade && this.infinite) {
      const { left } = this.slides[0].getBoundingClientRect();
      const { right } = this.slides[this.slides.length - 1].getBoundingClientRect();

      if (left > 0) {
        this.wrapInfinite();
      } else if (right < window.innerWidth + this.cell.step) {
        this.wrapInfinite("right");
      }
    }
  }

  onPointerUp(e) {
    try {
      this.track.releasePointerCapture(e.pointerId);
    } catch {}
    this.track.removeEventListener("pointermove", this._move);
    this.track.removeEventListener("pointerup", this._up);
    this.track.removeEventListener("pointercancel", this._up);
    this.track.removeEventListener("pointerleave", this._up);

    const wasDragging = !!this.pointer.dragging;
    this.pointer.dragging = false;
    this.track.classList.remove("is-dragging");

    if (!wasDragging) {
      this.vx = 0;
      this.samples = [];
      this.pointer.dragCanceled = false;
      this.track.removeEventListener("click", this._suppressClick, true);
      if (this.pauseOnInteraction) this.resumeAutoplay("interaction");
      return;
    }

    if (this.isFade) {
      const dx = this.pointer.curX - this.pointer.downX;
      const projectedDx = dx - this.vx * 180;
      const swipeThresholdPx = Math.max(
        30,
        ((this.viewport && this.viewport.clientWidth) || this.track.clientWidth || 0) *
          0.12,
      );
      const velocityTrigger = Math.abs(this.vx) > 0.25;

      if (Math.abs(projectedDx) >= swipeThresholdPx || velocityTrigger) {
        if (projectedDx < 0) {
          this.next(1, "drag");
        } else {
          this.prev(1, "drag");
        }
      } else {
        this.goTo(this.index, true, "drag");
      }

      this.vx = 0;
      this.samples = [];
      this.pointer.dragCanceled = false;
      this._suppressNextClick = true;
      if (this.pauseOnInteraction) this.resumeAutoplay("interaction");
      return;
    }

    const dx = this.pointer.curX - this.pointer.downX - this.vx;
    const rawDragSteps = -dx / this.cell.step;

    const MOMENTUM_MS = 320;
    const projectedPx = -this.vx * MOMENTUM_MS;
    const rawVelocitySteps = projectedPx / this.cell.step;

    const dead = this.thresholdRatio;
    const delta = rawDragSteps + rawVelocitySteps;

    if (Math.abs(delta) < dead) {
      this.goTo(this.index, true, "drag");
    } else if (this.infinite) {
      this.target = this.index + Math.round(rawDragSteps);
      this.goTo(this.target, true, "drag");
    } else {
      this.target = this.index + Math.round(delta);
      this.goTo(this.target, true, "drag");
    }

    this.vx = 0;
    this.samples = [];
    this.pointer.dragCanceled = false;
    this._suppressNextClick = true;
    if (this.pauseOnInteraction) this.resumeAutoplay("interaction");
  }

  cleanupPointer(e) {
    try {
      this.track.releasePointerCapture(e.pointerId);
    } catch {}
    this.track.removeEventListener("pointermove", this._move);
    this.track.removeEventListener("pointerup", this._up);
    this.track.removeEventListener("pointercancel", this._up);
    this.track.removeEventListener("pointerleave", this._up);
  }

  onResize() {
    this._applyResponsiveSettings();
    this.setTransition(false);
    this.measure();

    if (this.hasDots) {
      this.initDots();
    } else {
      this.clearDots();
    }

    if (this.isFade) {
      this.applyTransform(0);
      this.updateProgress();
      requestAnimationFrame(() => this.setTransition(true));
      return;
    }

    if (this.infinite && this.slides.length) {
      this.base = this.wraps * this.cell.step;
      this.visualIndex = this.index;

      const { left } = this.slides[0].getBoundingClientRect();
      const { right } = this.slides[this.slides.length - 1].getBoundingClientRect();

      if (left > 0) {
        this.wrapInfinite();
      } else if (right < window.innerWidth + this.cell.step) {
        this.wrapInfinite("right");
      }
    } else {
      this.index = Math.min(this.index, this.maxIndex);
      this.visualIndex = this.index;
    }

    this._updateArrowState();
    this.applyTransform(0);
    this.updateProgress();
    requestAnimationFrame(() => this.setTransition(true));
  }

  destroy() {
    this._destroyed = true;
    this.setTransition(false);
    this.track.classList.remove("is-dragging");
    this._clearAutoplayTimer();
    this._teardownAutoplayBindings();

    this.track.removeEventListener("pointermove", this._move);
    this.track.removeEventListener("pointerup", this._up);
    this.track.removeEventListener("pointercancel", this._up);
    this.track.removeEventListener("pointerleave", this._up);
    if (this._suppressClick) {
      this.track.removeEventListener("click", this._suppressClick, true);
    }
    if (this._down) {
      this.track.removeEventListener("pointerdown", this._down);
    }
    window.removeEventListener("resize", this._resize);

    if (this._btnHandlers && this.buttons) {
      this.buttons.forEach((b, i) =>
        b.removeEventListener("click", this._btnHandlers[i]),
      );
    }
    if (this._arrowHandlers) {
      if (this.prevButton) {
        this.prevButton.removeEventListener("click", this._arrowHandlers.prev);
      }
      if (this.nextButton) {
        this.nextButton.removeEventListener("click", this._arrowHandlers.next);
      }
    }

    if (this.slides) {
      this.slides.forEach((s) => {
        s.style.transition = "";
        s.style.transform = "";
        s.style.opacity = "";
        s.style.pointerEvents = "";
        s.style.zIndex = "";
        s.style.gridArea = "";
        s.style.willChange = "";
      });
    }

    if (this.isFade) {
      this.track.style.display = "";
      this.track.style.gridTemplateColumns = "";
    }

    this.clearDots();
    this.clearProgress();

    delete this.track.dataset.sliderInit;
    this.pointer = { downX: 0, curX: 0, dragging: false, dragCanceled: false };
    this.samples = [];
    this.vx = 0;
    this._changeListeners = [];
    this._dotHandlers = null;
  }
}

window.Slider = Slider;
